<template>
  <div class="my-4" v-if="emailsObject && (!isEmpty(emailsObject.emails) || emailsObject.has_emails)">
    <div class="card text-left">
      <div class="card-body py-3">
        <h3 class="mb-3 t-500">
          {{ title }}
          <!-- (This can be deprecated) <i v-if="tooltipText" class="fa fa-info-circle t-secondary t-xs" v-tooltip="tooltipText" /> -->
        </h3>
        <!-- If emails is empty, prompt to contact sales -->
        <a
          v-if="isEmpty(emailsObject.emails) && emailsObject.has_emails"
          href="https://www.academiclabs.com/contact-sales" target="_blank"
        >Contact us about the email add-on</a>
        <!-- Else show emails -->
        <div v-for="email in emails" :key="email.email">
          <!-- <i class="fa fa-envelope t-secondary t-xs pr-2" /> -->
          <dropdown
            placement="bottom-end" noPadding
            style="display: inline-block;"
            trigger="manual"
            :open="email.dropDownOpen">
            <template #button>
              <a v-tooltip="tooltipCopy(email)" @click="tryToOpenDropDown(email)">
                <i :class="`fa ${iconStyle(email)} t-sm is-hover--blue mr-2`"></i>
                <span v-if="!email.email" class="t-secondary is-hover--blue">Find email of {{ email.full_name }}</span>
              </a>
            </template>
            <div class="dropdown-menu-custom box-shadow p-3">
              <div class="t-sm">Find a verified email?<br /><span class="t-xs">
                <template v-if="email.email"><i>{{email.email}}</i> may no longer be valid.<br />Should we look for an active, verified email?</template>
                <template v-else>We currently don't have a valid email for {{ email.full_name }}. Should we find one?</template>
                This operation takes a few minutes, and consumes 1 contact credit if successful.<br />
                </span></div>
              <button
                class="btn btn-primary no-border ml-0 mt-2"
                type="button"
                @click="findVerifiedEmail(email)">Yes
              </button>
              <button
                class="btn bg-transparent no-border ml-3 mt-2"
                type="button"
                v-close-popover>No
              </button>
            </div>
          </dropdown>
          <a class="t-overflow" :href="`mailto:${email.email}`" target="_blank">{{ email.email }}</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Dropdown from '@/components/shared/form/Dropdown'
import EmailService from '@/services/email'

const STATUS = {
  unverified: 'unverified',
  busy: 'busy',
  verified: 'verified',
  catchall: 'catchall',
  unknown: 'unknown'
}

export default {
  name: 'activeEmailAddress',
  components: {
    Dropdown
  },
  props: {
    title: {
      type: String,
      required: false,
      default: 'Email address'
    },
    emailsObject: {
      type: Object,
      default: () => {},
      required: false
    }
  },
  data () {
    return {
      emails: []
    }
  },
  created () {
    // Make a copy of the email objects, with additional fields
    this.emails = this.emailsObject.emails.map(item => ({
      ...item,
      status: STATUS.unverified, // Validation status of this email
      dropDownOpen: false // Open the dropdown attached to this
    }))
  },
  computed: {
    tooltipText () {
      const filteredEmails = this.emailsObject.emails.filter(obj => obj.email_validity)
      if (filteredEmails.length === 1) {
        return `Active email validity: ${filteredEmails[0].email_validity}`
      } else if (filteredEmails.length > 1) {
        const validityText = filteredEmails.map(email => {
          return `${email.email_validity} (${email.email})`
        }).join(', ')

        return `Active email validity: ${validityText}`
      }
      return ''
    },
    iconStyle () {
      return (mailObj) => {
        if (!mailObj.email) {
          return 'fa-envelope t-secondary'
        }
        switch (mailObj.status) {
          case STATUS.busy:
            return 'fa-refresh rotate-animation t-blue'
          case STATUS.verified:
            return 'fa-check-circle t-green'
          case STATUS.catchall:
            return 'fa-check-circle t-secondary'
          case STATUS.unknown:
            return 'fa-question-circle t-secondary'
          default:
            return 'fa-refresh t-secondary'
        }
      }
    },
    tooltipCopy () {
      return (mailObj) => {
        if (!mailObj.email) {
          return 'Find a verified email address'
        }
        switch (mailObj.status) {
          case STATUS.busy:
            return 'Verifying...'
          case STATUS.verified:
            return `${mailObj.email} is a verified email address`
          case STATUS.catchall:
            return `${mailObj.email} is a verified catch-all email address`
          case STATUS.unknown:
            return `${mailObj.email} may no longer be valid`
          default:
            return 'Verify whether this email address is still valid<br />(verification does not use any credits)'
        }
      }
    }
  },
  methods: {
    tryToOpenDropDown (mailObj) {
      // Only open the dropdown if there is no email, or we can't verify it
      mailObj.dropDownOpen = false
      if (this.dropDownisDisabled(mailObj)) {
        return
      }

      if (mailObj.email && mailObj.status === STATUS.unverified) {
        mailObj.status = STATUS.busy
        EmailService.verifyEmail({
          email: mailObj.email
        }).then(result => {
          if (result.isValid) {
            mailObj.status = STATUS.verified
          } else {
            mailObj.status = STATUS.unknown
            mailObj.dropDownOpen = true
          }
        }).catch(err => {
          this.$flash({ message: this.renderError(err) }, 'warning')
        })
      } else {
        setTimeout(() => {
          mailObj.dropDownOpen = true
        }, 50) // Needs a tiny delay; immediately flipping the boolean on->off->on won't re-open the dropdown
      }
    },
    dropDownisDisabled (mailObj) {
      return [STATUS.verified, STATUS.catchall, STATUS.busy].includes(mailObj.status)
    },
    findVerifiedEmail (mailObj) {
      mailObj.status = STATUS.busy

      EmailService.findEmailBySlug({
        fullName: mailObj.full_name,
        email: mailObj.email,
        slugValues: mailObj.slug_values
      }).then(result => {
        // If we could validate the email, show it to the user.
        // Otherwise, BE will carry on in the background, trying to find a valid email.
        if (result.validate_status) {
          if (result.validate_status === 'valid') {
            mailObj.status = STATUS.verified
          } else if (result.validate_status === 'catch_all') {
            mailObj.status = STATUS.catchall
          }
        } else {
          mailObj.status = STATUS.unverified
        }
      }).catch(err => {
        mailObj.status = STATUS.unverified
        this.$flash({ message: this.renderError(err) }, 'warning')
      })
    }
  }
}
</script>
